/**
 * `/payroll` — the Payroll screen, WP-W9 (Phase 8a SPA).
 * `web/shapes/payroll.md` is the agreed shape, written before this file.
 *
 * The one payroll surface every employee reaches, not only an administrator:
 * `src/lib/hiring-permissions.ts`'s `SELF_SERVICE_FLOOR` gives every role
 * `payroll.view_own_payslip` before any grant row, so `nav/menu.ts` declares this
 * item with no role check at all. Four sections:
 *
 *  - **My payslip** (`MyPayslipPanel`) — always rendered. Self-only, structurally:
 *    `payrollMyPayslipPath` (`api/payroll.ts`) has no employee-id parameter at all,
 *    so there is no argument position this screen could use to name anyone but the
 *    session even by accident.
 *  - **Manage** (`TemplateManagePanel`) — rendered only when the session holds
 *    `payroll.manage_templates`, the same client-side honesty gate `LeaveScreen`'s
 *    approvals section and `PerformanceScreen`'s Manage section already use — never
 *    the authorisation, which the server still runs on every call.
 *  - **Formula versions** (`PayrollFormulasPanel`, Task U18, roadmap E0-4) — rendered
 *    only when the session holds `payroll.manage_regulations`, which gates the reads and
 *    the writes of `/payroll/formulas*` alike. The history, the version in force, a
 *    second person's "Xác nhận & kích hoạt", and drafts dated for someone else to adopt.
 *  - **Runs** (`RunsPanel`) — rendered when the session holds any of
 *    `payroll.view_payroll` / `run_payroll` / `approve_payroll` / `ceo_approve`.
 *    Periods, run create, the manual inputs a calculation sends (`RunInputsPanel`,
 *    roadmap A-6: the grid, CSV, and the press that calculates), and the two-step
 *    approval (manager-approve, then CEO-approve), each control additionally gated on the
 *    right permission AND the run's current `status` — see `RunsPanel`'s own header.
 *
 * **Payslip PDFs (roadmap U26).** My payslip and each team payslip of a released run
 * offer a PDF through `downloadPayslipPdf` (`payroll/payslip-download.ts`) — the HR
 * document download's mechanism, reason gate included. One download runs at a time, and
 * its outcome is keyed to the payslip it was for. `deliverPayslip` is injectable the way
 * `EmployeeDocumentsScreen`'s `deliverDocument` is, because jsdom has no
 * `URL.createObjectURL`.
 *
 * `payroll.manage_templates` gates BOTH reading and writing the template
 * (`src/modules/payroll/authorizer.ts`'s `createSessionTemplateViewAuthorizer` uses
 * the identical code list as `createSessionTemplateManageAuthorizer`), unlike the
 * leave/performance surfaces' split view/manage codes — so there is exactly one gate
 * for the whole Manage section, not two independent ones.
 *
 * The templates, periods and runs reads fire regardless of which of the four
 * sections a given session can see — the same "independent regions" shape
 * `PerformanceScreen` already keeps for its own Manage/Review sections — so a `403`
 * on a read the session cannot make simply never reaches the screen, because the
 * panel that would show it is not mounted.
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../i18n';
import { useSession } from '../auth/SessionProvider';
import type {
  PayrollCalculateRunRequest,
  PayrollCalculateRunResponse,
  PayrollComponentDef,
  PayrollComponentDefCreateRequest,
  PayrollComponentDefListResponse,
  PayrollItem,
  PayrollItemListResponse,
  PayrollMyPayslipResponse,
  PayrollPeriod,
  PayrollPeriodCreateRequest,
  PayrollPeriodListResponse,
  PayrollRosterResponse,
  PayrollRun,
  PayrollRunCreateRequest,
  PayrollRunListResponse,
  PayrollTemplate,
  PayrollTemplateCreateRequest,
  PayrollTemplateListResponse,
} from '../api/payroll';
import {
  PAYROLL_ENGINE_COUNTRY,
  PAYROLL_PERIODS_PATH,
  PAYROLL_RUNS_PATH,
  PAYROLL_TEMPLATES_PATH,
  payrollComponentDefsPath,
  payrollMyPayslipPath,
  payrollMyPayslipPdfFilename,
  payrollMyPayslipPdfPath,
  payrollRunCalculatePath,
  payrollRunCeoApprovePath,
  payrollRunItemPayslipPdfPath,
  payrollRunItemsPath,
  payrollRunManagerApprovePath,
  payrollRunRosterPath,
  payrollRunsPath,
  payslipPdfFilename,
} from '../api/payroll';
import { useApiMutation } from '../api/useApiMutation';
import { useApiQuery } from '../api/useApiQuery';
import type { InputGrid } from '../payroll/employee-inputs';
import {
  deliverBlobDownload,
  downloadPayslipPdf,
  myPayslipDownloadKey,
  type DeliverBlob,
  type PayslipDownloadRequest,
  type PayslipDownloadState,
} from '../payroll/payslip-download';
import { useCompanySettings } from '../api/company-settings';
import { ScreenHeading } from '../shell/ScreenHeading';
import { StatusNote } from '../states/StatusNote';
import { MyPayslipPanel } from './payroll/MyPayslipPanel';
import { PayrollFormulasPanel } from './payroll/PayrollFormulasPanel';
import { TemplateManagePanel } from './payroll/TemplateManagePanel';
import { RunsPanel, type RunAction } from './payroll/RunsPanel';

export interface PayrollScreenProps {
  /**
   * How a fetched payslip PDF is handed to the person. Injected so a test can drive the
   * download without `URL.createObjectURL`, which jsdom does not implement.
   */
  readonly deliverPayslip?: DeliverBlob;
}

export function PayrollScreen({
  deliverPayslip = deliverBlobDownload,
}: PayrollScreenProps = {}): ReactNode {
  const { t } = useI18n();
  const { user, client } = useSession();
  const permissions = user?.permissions ?? [];
  const mayManageTemplates = permissions.includes('payroll.manage_templates');
  const canRunPayroll = permissions.includes('payroll.run_payroll');
  const canApprove = permissions.includes('payroll.approve_payroll');
  const canCeoApprove = permissions.includes('payroll.ceo_approve');
  const canViewItems = permissions.includes('payroll.view_payroll');
  const mayRuns = canRunPayroll || canApprove || canCeoApprove || canViewItems;
  /**
   * Task U18. `payroll.manage_regulations` gates BOTH the reads and the writes of
   * `/payroll/formulas*` (`authorizer.ts:129,134` name the identical code list), so
   * there is one gate for the whole section rather than a split view/manage pair —
   * the same shape `payroll.manage_templates` already has above. Client-side honesty
   * only; the server still authorises every call.
   */
  const mayManageRegulations = permissions.includes('payroll.manage_regulations');

  /*
    CS-3 — the engine is one country's law, and this screen says which BEFORE anyone
    fills in a run. `useCompanySettings` is already fetched on this screen (the three
    panels read the currency from it), so this costs no request. `country` is `null`
    while the read is in flight or if it failed: an unknown country claims nothing —
    the notice appears only when the company's own configuration is actually known to
    name a country with no rule set, and the server refuses either way.
  */
  const companySettings = useCompanySettings();
  const country = companySettings.data?.country_code ?? null;
  const countryUnsupported = country !== null && country !== PAYROLL_ENGINE_COUNTRY;

  /* ── My payslip (every employee, self-only) ──────────────────────────────── */

  const [myPeriodId, setMyPeriodId] = useState<string | null>(null);
  const myPayslipPath = myPeriodId === null ? null : payrollMyPayslipPath(myPeriodId);
  const myPayslip = useApiQuery<PayrollMyPayslipResponse>(myPayslipPath);

  /* ── Manage (payroll.manage_templates) ───────────────────────────────────── */

  const [templatesToken, setTemplatesToken] = useState(0);
  const templates = useApiQuery<PayrollTemplateListResponse>(
    PAYROLL_TEMPLATES_PATH,
    templatesToken,
  );
  const createTemplate = useApiMutation<PayrollTemplateCreateRequest, PayrollTemplate>(
    PAYROLL_TEMPLATES_PATH,
  );
  const onCreateTemplate = (body: PayrollTemplateCreateRequest): void => {
    createTemplate
      .submit(body)
      .then(() => {
        setTemplatesToken((value) => value + 1);
      })
      .catch(() => {
        /* `submit` resolves rather than rejects. */
      });
  };

  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [defsToken, setDefsToken] = useState(0);
  const defsPath =
    selectedTemplateId === null ? null : payrollComponentDefsPath(selectedTemplateId);
  const defs = useApiQuery<PayrollComponentDefListResponse>(defsPath, defsToken);
  // Bound to a placeholder path (never actually requested at that path) exactly as
  // `LeaveScreen`'s `cancelRequest` binds to `hrLeaveRequestCancelPath('unset')` —
  // `onCreateDef` below always passes the real, selected template's path as the
  // per-submit `pathOverride`.
  const createDef = useApiMutation<PayrollComponentDefCreateRequest, PayrollComponentDef>(
    payrollComponentDefsPath(selectedTemplateId ?? 'unset'),
  );

  const onSelectTemplate = (id: string): void => {
    createDef.reset();
    setSelectedTemplateId(id);
  };

  const onCreateDef = (body: PayrollComponentDefCreateRequest): void => {
    if (selectedTemplateId === null) return;
    createDef
      .submit(body, payrollComponentDefsPath(selectedTemplateId))
      .then(() => {
        setDefsToken((value) => value + 1);
      })
      .catch(() => {
        /* resolves rather than rejects */
      });
  };

  /* ── Runs — periods ───────────────────────────────────────────────────────── */

  const [periodsToken, setPeriodsToken] = useState(0);
  const periods = useApiQuery<PayrollPeriodListResponse>(PAYROLL_PERIODS_PATH, periodsToken);
  const createPeriod = useApiMutation<PayrollPeriodCreateRequest, PayrollPeriod>(
    PAYROLL_PERIODS_PATH,
  );
  const onCreatePeriod = (body: PayrollPeriodCreateRequest): void => {
    createPeriod
      .submit(body)
      .then(() => {
        setPeriodsToken((value) => value + 1);
      })
      .catch(() => {
        /* resolves rather than rejects */
      });
  };

  /* ── Runs — the runs list, create, calculate + two-step approval ─────────── */

  const [runsFilterPeriodId, setRunsFilterPeriodId] = useState<string | null>(null);
  const [runsToken, setRunsToken] = useState(0);
  const runsPath = payrollRunsPath(runsFilterPeriodId ?? undefined);
  const runs = useApiQuery<PayrollRunListResponse>(runsPath, runsToken);
  const onFilterRuns = (periodId: string | null): void => {
    setRunsFilterPeriodId(periodId);
  };

  const createRun = useApiMutation<PayrollRunCreateRequest, PayrollRun>(PAYROLL_RUNS_PATH);
  const onCreateRun = (body: PayrollRunCreateRequest): void => {
    createRun
      .submit(body)
      .then(() => {
        setRunsToken((value) => value + 1);
      })
      .catch(() => {
        /* resolves rather than rejects */
      });
  };

  const calculate = useApiMutation<PayrollCalculateRunRequest, PayrollCalculateRunResponse>(
    payrollRunCalculatePath('unset'),
  );
  const managerApprove = useApiMutation<undefined, PayrollRun>(
    payrollRunManagerApprovePath('unset'),
  );
  const ceoApprove = useApiMutation<undefined, PayrollRun>(payrollRunCeoApprovePath('unset'));

  /*
    Each write's target, captured at its press: the outcome stays with the run it was
    pressed for. `calculate` and the approvals are mutations reused across runs — the
    multi-target shape the residual review says `useApiMutation` has no generation guard
    for — so one run write goes at a time (fix round 1, R21): while any is in flight no
    other can be pressed, and no press resets another kind's outcome.
  */
  /** The run the last approval was sent for. */
  const [approvalRunId, setApprovalRunId] = useState<string | null>(null);
  /** The run the last calculation was sent for. */
  const [calculatingRunId, setCalculatingRunId] = useState<string | null>(null);
  const calculationPending = calculate.status === 'pending';
  const approvalPending = managerApprove.status === 'pending' || ceoApprove.status === 'pending';
  /** The run whose write is in flight, or `null` — what every write control waits on. */
  const pendingRunId = calculationPending
    ? calculatingRunId
    : approvalPending
      ? approvalRunId
      : null;
  const writePending = calculationPending || approvalPending;
  /** The row's own refusal. A calculation's renders in the inputs panel, where it was pressed. */
  const approvalError = managerApprove.error ?? ceoApprove.error;

  const reloadRuns = (): void => {
    setRunsToken((value) => value + 1);
  };

  const onRunAction = (runId: string, action: RunAction): void => {
    // The controls are absent while a write is in flight; this is the belt to that brace.
    if (writePending) return;
    managerApprove.reset();
    ceoApprove.reset();
    setApprovalRunId(runId);
    const approval = action === 'manager_approve' ? managerApprove : ceoApprove;
    const path =
      action === 'manager_approve'
        ? payrollRunManagerApprovePath(runId)
        : payrollRunCeoApprovePath(runId);
    approval
      .submit(undefined, path)
      .then(reloadRuns)
      .catch(() => {
        /* resolves rather than rejects; the error stays attributed to this row. */
      });
  };

  /* ── Runs — the manual inputs a calculation sends (roadmap A-6 / B3) ──────── */

  /** The run whose input grid is open — one at a time. */
  const [inputsRunId, setInputsRunId] = useState<string | null>(null);
  /**
   * Every grid typed on this screen, by run: closing and reopening a run's grid loses
   * nothing. Kept in memory only and never written to browser storage — these are salary
   * figures.
   */
  const [inputGrids, setInputGrids] = useState<Readonly<Record<string, InputGrid>>>({});
  const roster = useApiQuery<PayrollRosterResponse>(
    inputsRunId === null ? null : payrollRunRosterPath(inputsRunId),
  );
  /** A calculation's outcome is shown only beside the run it was sent for. */
  const isCalculationOf = (runId: string | null): boolean =>
    runId !== null && calculatingRunId === runId;

  const onCalculate = (runId: string, body: PayrollCalculateRunRequest): void => {
    if (writePending) return;
    // Only this kind's last outcome is replaced; an approval's stays on its own row.
    calculate.reset();
    setCalculatingRunId(runId);
    calculate
      .submit(body, payrollRunCalculatePath(runId))
      .then(reloadRuns)
      .catch(() => {
        /* resolves rather than rejects; the refusal renders in the inputs panel. */
      });
  };

  /* ── Runs — a run's items (team payslips), payroll.view_payroll ──────────── */

  const [itemsRunId, setItemsRunId] = useState<string | null>(null);
  const [itemsToken, setItemsToken] = useState(0);
  const itemsPath = itemsRunId === null ? null : payrollRunItemsPath(itemsRunId);
  const items = useApiQuery<PayrollItemListResponse>(itemsPath, itemsToken);
  const onViewItems = (runId: string): void => {
    setItemsRunId(runId);
  };
  const onCloseItems = (): void => {
    setItemsRunId(null);
  };
  const onReloadItems = (): void => {
    setItemsToken((value) => value + 1);
  };

  /* ── Payslip PDFs (roadmap U26) — mine and a team one, one at a time ─────────── */

  const [payslipDownload, setPayslipDownload] = useState<PayslipDownloadState>({
    status: 'idle',
  });
  const downloadPayslip = (request: PayslipDownloadRequest, reason: string): void => {
    setPayslipDownload({ status: 'requesting', key: request.key });
    downloadPayslipPdf(client, request, reason, deliverPayslip)
      .then(setPayslipDownload)
      .catch(() => {
        /* resolves with a `refused` state rather than rejecting. */
      });
  };
  const onDownloadMyPdf = (reason: string): void => {
    if (myPeriodId === null) return;
    downloadPayslip(
      {
        // R11, fix round 1: `myPayslipDownloadKey` is the one place this is built, so
        // this and `MyPayslipPanel`'s own `mine` check can never drift apart.
        key: myPayslipDownloadKey(myPeriodId),
        path: payrollMyPayslipPdfPath(myPeriodId),
        filename: payrollMyPayslipPdfFilename(myPeriodId),
      },
      reason,
    );
  };
  const onDownloadItemPdf = (run: PayrollRun, item: PayrollItem, reason: string): void => {
    downloadPayslip(
      {
        key: item.id,
        path: payrollRunItemPayslipPdfPath(run.id, item.employee_id),
        filename: payslipPdfFilename(run.period_id, item.employee_id),
      },
      reason,
    );
  };

  return (
    // `wp-d` — the group-D scope, the same device `.wp-b` and `.wp-f` use, so this
    // screen's section/subsection type steps can be set without restyling every
    // subsection in the product (finish review, minor f).
    <div className="wp-stack wp-d">
      <ScreenHeading>{t('nav.payroll')}</ScreenHeading>
      <p className="wp-small wp-muted">{t('payroll.intro')}</p>
      <p className="wp-small wp-muted" data-testid="w9-scope-note">
        {t('payroll.scopeNote')}
      </p>

      {countryUnsupported ? (
        <StatusNote
          severity="high"
          title={t('payroll.countryEngine.title')}
          testId="w9-country-engine-note"
        >
          <p className="wp-small">{t('payroll.countryEngine.body', { country })}</p>
        </StatusNote>
      ) : null}

      <MyPayslipPanel
        periodId={myPeriodId}
        onLoadPeriod={setMyPeriodId}
        periods={periods.data?.data ?? null}
        status={myPayslip.status}
        payslip={myPayslip.data?.data ?? null}
        error={myPayslip.error}
        onReload={myPayslip.reload}
        pdfDownload={payslipDownload}
        onDownloadPdf={onDownloadMyPdf}
      />

      {mayManageTemplates ? (
        <TemplateManagePanel
          templatesStatus={templates.status}
          templates={templates.data?.data ?? null}
          templatesError={templates.error}
          onReloadTemplates={templates.reload}
          templateCreatePending={createTemplate.status === 'pending'}
          templateCreateError={createTemplate.error}
          templateCreated={createTemplate.status === 'ok' ? createTemplate.data : null}
          onCreateTemplate={onCreateTemplate}
          selectedTemplateId={selectedTemplateId}
          onSelectTemplate={onSelectTemplate}
          defsStatus={defs.status}
          defs={defs.data?.data ?? null}
          defsError={defs.error}
          onReloadDefs={defs.reload}
          defCreatePending={createDef.status === 'pending'}
          defCreateError={createDef.error}
          defCreated={createDef.status === 'ok' ? createDef.data : null}
          onCreateDef={onCreateDef}
        />
      ) : null}

      {mayManageRegulations ? <PayrollFormulasPanel canViewRuns={canViewItems} /> : null}

      {mayRuns ? (
        <RunsPanel
          canRunPayroll={canRunPayroll}
          canApprove={canApprove}
          canCeoApprove={canCeoApprove}
          canViewItems={canViewItems}
          periodsStatus={periods.status}
          periods={periods.data?.data ?? null}
          periodsError={periods.error}
          onReloadPeriods={periods.reload}
          periodCreatePending={createPeriod.status === 'pending'}
          periodCreateError={createPeriod.error}
          periodCreated={createPeriod.status === 'ok' ? createPeriod.data : null}
          onCreatePeriod={onCreatePeriod}
          runsStatus={runs.status}
          runs={runs.data?.data ?? null}
          runsError={runs.error}
          onReloadRuns={runs.reload}
          runsFilterPeriodId={runsFilterPeriodId}
          onFilterRuns={onFilterRuns}
          runCreatePending={createRun.status === 'pending'}
          runCreateError={createRun.error}
          runCreated={createRun.status === 'ok' ? createRun.data : null}
          onCreateRun={onCreateRun}
          countryUnsupported={countryUnsupported}
          pendingRunId={pendingRunId}
          approvalRunId={approvalRunId}
          approvalError={approvalError}
          onRunAction={onRunAction}
          inputsRunId={inputsRunId}
          onOpenInputs={setInputsRunId}
          onCloseInputs={() => {
            setInputsRunId(null);
          }}
          rosterStatus={roster.status}
          roster={roster.data?.data ?? null}
          rosterError={roster.error}
          onReloadRoster={roster.reload}
          inputGrid={inputsRunId === null ? {} : (inputGrids[inputsRunId] ?? {})}
          onInputGridChange={(grid) => {
            if (inputsRunId === null) return;
            setInputGrids((current) => ({ ...current, [inputsRunId]: grid }));
          }}
          calculateError={isCalculationOf(inputsRunId) ? calculate.error : null}
          calculatedCount={
            isCalculationOf(inputsRunId) && calculate.status === 'ok' && calculate.data !== null
              ? calculate.data.items.length
              : null
          }
          onCalculate={onCalculate}
          itemsRunId={itemsRunId}
          onViewItems={onViewItems}
          onCloseItems={onCloseItems}
          itemsStatus={items.status}
          items={items.data?.data ?? null}
          itemsError={items.error}
          onReloadItems={onReloadItems}
          pdfDownload={payslipDownload}
          onDownloadItemPdf={onDownloadItemPdf}
        />
      ) : null}
    </div>
  );
}
